import type { ISODateTimeString } from '@gat-ship/types'
import { Temporal } from '@js-temporal/polyfill'
import {
  getDateFormatPattern,
  maskDateInput,
  parseDateInput
} from '@gat-ship/utils'
import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'
import { formatDate, getCurrentTimeZoneId } from './date-utils'

/** Props for {@link useDateInput}. */
export interface UseDateInputProps {
  /** The bound value — a UTC instant string, or `undefined` when empty. */
  modelValue: MaybeRefOrGetter<ISODateTimeString | undefined>
  /**
   * Locale that drives the display format and the field order used to
   * expand partial input (e.g. `'de-DE'`). Falls back to the runtime
   * locale when `undefined`.
   */
  locale?: MaybeRefOrGetter<string | undefined>
  /** Timezone the date is displayed and interpreted in. */
  timeZone?: MaybeRefOrGetter<string | undefined>
  /**
   * Time-of-day a freshly-typed date is anchored to when the model is
   * empty. `'noon'` keeps the value on the same calendar day in any
   * timezone; `'midnight'` sits on the day boundary. An existing value
   * keeps its own time-of-day regardless. Defaults to `'noon'`.
   */
  anchorTime?: 'midnight' | 'noon'
  /**
   * When `true`, typing a lone day that has already passed this month
   * rolls it forward to the next month — useful for fields that should
   * land in the future. Off by default.
   */
  futureDate?: boolean
}

/**
 * Backs a plain text date input. Formats the bound instant for display
 * and, on blur, expands whatever the user typed into a full date.
 *
 * The date counterpart of `useTimeInput`, built for
 * the focus/input/blur pattern the form-field inputs share: bind
 * `dateInputValue` one-way and forward the three handlers. While the
 * field has focus it shows the user's keystrokes verbatim; on blur it
 * commits via `updateModel` and the display snaps to the canonical
 * value. Typing one number sets the day, two sets day + month (in
 * locale order), three sets the whole date; unparseable input is
 * dropped, leaving the model untouched.
 */
export function useDateInput(
  props: UseDateInputProps,
  updateModel: (value: ISODateTimeString | undefined) => void
) {
  const locale = computed(
    () =>
      toValue(props.locale) ?? Intl.DateTimeFormat().resolvedOptions().locale
  )
  const timeZone = computed(
    () => toValue(props.timeZone) ?? getCurrentTimeZoneId()
  )

  /** The bound instant as a zoned date-time, or `undefined` when empty. */
  const zonedModel = computed(() => {
    const value = toValue(props.modelValue)
    return value
      ? Temporal.Instant.from(value).toZonedDateTimeISO(timeZone.value)
      : undefined
  })

  /** The model formatted for the locale, or `''` when empty. */
  const formattedModel = computed(() => {
    const zoned = zonedModel.value
    return zoned ? formatDate(zoned, locale.value, true) : ''
  })

  // While the field has focus the user's keystrokes are shown verbatim
  // (`draft`); once it blurs, the display snaps back to the canonical
  // `formattedModel`.
  const isEditing = ref(false)
  const draft = ref('')

  /** What the bound text input should display. */
  const dateInputValue = computed(() =>
    isEditing.value ? draft.value : formattedModel.value
  )

  /** The locale's date format, e.g. `DD.MM.YYYY` — drives masking. */
  const datePattern = computed(() => getDateFormatPattern(locale.value))

  /**
   * Format hint for the input's placeholder. Lowercased — `dd.mm.yyyy`
   * reads as a softer hint than the uppercase tokens the util returns.
   */
  const formatPattern = computed(() => datePattern.value.toLowerCase())

  /** Call when the input gains focus. */
  function onFocus() {
    draft.value = formattedModel.value
    isEditing.value = true
  }

  /**
   * Call with the input's current text whenever it changes.
   *
   * - **Mid-string edit** (`caretAtEnd === false`): pass through untouched —
   *   re-masking there would repack digits across separators and jump the caret.
   * - **Deletion at the end** (value got shorter): don't re-insert separators
   *   (that would fight the backspace against eager separators); just drop a
   *   now-trailing separator so the next press removes a digit.
   * - **Append at the end**: eagerly mask, so `01` → `01.` and a digits-only
   *   keypad needs no separator key. Keyboard-agnostic — identical on desktop.
   *
   * Blur still parses the value in every case.
   */
  function onInput(value: string, caretAtEnd = true) {
    if (!caretAtEnd) {
      draft.value = value
      return
    }
    if (value.length < draft.value.length) {
      draft.value = value.replace(/\D+$/, '')
      return
    }
    draft.value = maskDateInput(value, datePattern.value, { eager: true })
  }

  /** Call when the input loses focus — commits the typed value. */
  function onBlur() {
    isEditing.value = false
    const typed = draft.value

    // Unchanged from the canonical display — nothing to commit.
    if (typed === formattedModel.value) return

    // Cleared — drop the model.
    if (!typed.trim()) {
      updateModel(undefined)
      return
    }

    const now = Temporal.Now.zonedDateTimeISO(timeZone.value)
    const parsed = parseDateInput(typed, {
      locale: locale.value,
      reference: { year: now.year, month: now.month, day: now.day },
      futureDate: props.futureDate
    })

    // Unparseable — leave the model untouched. Clearing `isEditing`
    // already snapped the display back to the canonical value.
    if (!parsed) return

    // Replace only the date, keeping any time-of-day the model carried
    // so a later date edit never wipes the time. A fresh value is
    // anchored to noon by default — noon stays on the same calendar day
    // in any timezone, whereas midnight can drift across the boundary.
    const anchorHour = (props.anchorTime ?? 'noon') === 'midnight' ? 0 : 12
    const base =
      zonedModel.value ??
      now.with({
        hour: anchorHour,
        minute: 0,
        second: 0,
        millisecond: 0,
        microsecond: 0,
        nanosecond: 0
      })
    const next = base.with({
      year: parsed.year,
      month: parsed.month,
      day: parsed.day
    })

    updateModel(next.toInstant().toString() as ISODateTimeString)
  }

  return { dateInputValue, formatPattern, onFocus, onInput, onBlur }
}
